import { useCallback } from 'react';

import { useUserPreferences } from 'app/services/userPreferences';
import {
  OabCaptureStep,
  OabCaptureSurface,
  trackOabCaptureError,
} from 'app/services/tracking/oabCapture/oabCaptureEvents';

type UseOabCaptureShownMarksParams = {
  surface: OabCaptureSurface;
  step: OabCaptureStep;
  markBannerShown: () => void;
};

type UseOabCaptureShownMarks = {
  recordShown: () => Promise<void>;
};

// O backend calcula should_show_banner a partir destas marcas e é dono do
// write-once de oabEligibilityBannerFirstShownAt (FT-910): o front não distingue
// uma marca ausente de uma ainda não carregada, então sempre envia o par.
// markBannerShown vem por parâmetro (e não de um useOabEligibility aqui dentro)
// porque um segundo observer da query seria efeito escondido — e dispararia o GET
// antes de um gatilho armar o modal.
export function useOabCaptureShownMarks({
  surface,
  step,
  markBannerShown,
}: UseOabCaptureShownMarksParams): UseOabCaptureShownMarks {
  const { updateUserPreferencesAsync } = useUserPreferences();

  const recordShown = useCallback(async (): Promise<void> => {
    const now = new Date().toISOString();

    try {
      await updateUserPreferencesAsync({
        oabEligibilityBannerFirstShownAt: now,
        oabEligibilityBannerLastShownAt: now,
      });
    } catch (error) {
      // PATCH falhou: não fecha a janela de elegibilidade local por uma marca
      // que o backend nunca persistiu, senão o banner some da sessão sem que
      // should_show_banner tenha de fato mudado.
      trackOabCaptureError({
        surface,
        step,
        errorAction: 'shown_mark_persist',
        error,
      });
      return;
    }

    markBannerShown();
  }, [updateUserPreferencesAsync, markBannerShown, surface, step]);

  return { recordShown };
}
